import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { categories, fetchCatalogData, Product } from "../../data/catalog";
import ProductCard from "./ProductCard";
import ProductDetailModal from "./ProductDetailModal";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertCircle,
  X,
} from "lucide-react";
import "../../utils/errorHandler";
import { normalizeText, isSpecialEdition } from "../../utils/categoryUtils";

interface CatalogProps {
  activeCategory?: string;
  setActiveCategory?: (cat: string) => void;
}

const Catalog: React.FC<CatalogProps> = ({
  activeCategory: propActiveCategory,
  setActiveCategory: propSetActiveCategory,
}) => {
  const [localActiveCategory, setLocalActiveCategory] = useState("all");
  const activeCategory = propActiveCategory !== undefined ? propActiveCategory : localActiveCategory;

  const setActiveCategory = (cat: string) => {
    if (propSetActiveCategory) {
      propSetActiveCategory(cat);
    } else {
      setLocalActiveCategory(cat);
    }
  };

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProductForDetail, setSelectedProductForDetail] = useState<Product | null>(null);
  const productsPerPage = 12;

  // Sincronizar modal de detalle con URL / Hash
  const checkUrlForProduct = useCallback((productList: Product[]) => {
    if (!productList || productList.length === 0) return;

    const hash = window.location.hash;
    const pathname = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    const queryProduct = searchParams.get("postre") || searchParams.get("producto");

    let targetId: string | null = null;

    if (hash.startsWith("#postre-")) {
      targetId = hash.replace("#postre-", "");
    } else if (hash.startsWith("#detail-")) {
      targetId = hash.replace("#detail-", "");
    } else if (pathname.startsWith("/postre/")) {
      targetId = pathname.replace("/postre/", "").replace(/\/$/, "");
    } else if (queryProduct) {
      targetId = queryProduct;
    }

    if (targetId) {
      const found = productList.find((p) => p.id === targetId);
      setSelectedProductForDetail(found && found.available !== false ? found : null);
    }
  }, []);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchCatalogData();
        setProducts(data);
        checkUrlForProduct(data);
      } catch (err) {
        setError(
          "Error al cargar el catálogo. Por favor, intenta de nuevo más tarde.",
        );
        console.error("Error loading products:", err);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [checkUrlForProduct]);

  // Escuchar cambios de hash o popstate para abrir modal automáticamente
  useEffect(() => {
    const handleUrlChange = () => {
      checkUrlForProduct(products);
    };

    window.addEventListener("hashchange", handleUrlChange);
    window.addEventListener("popstate", handleUrlChange);

    return () => {
      window.removeEventListener("hashchange", handleUrlChange);
      window.removeEventListener("popstate", handleUrlChange);
    };
  }, [products, checkUrlForProduct]);

  const handleOpenProductDetail = (product: Product) => {
    if (product.available === false) return;
    setSelectedProductForDetail(product);
    window.history.replaceState(null, "", `#postre-${product.id}`);
  };

  const handleCloseProductDetail = () => {
    setSelectedProductForDetail(null);
    if (window.location.hash.startsWith("#postre-") || window.location.hash.startsWith("#detail-")) {
      window.history.replaceState(null, "", "#catalogo");
    }
  };

  const handleNavigateDetail = (direction: "next" | "prev") => {
    if (!selectedProductForDetail || sortedFiltered.length === 0) return;
    const currentIndex = sortedFiltered.findIndex((p) => p.id === selectedProductForDetail.id);
    if (currentIndex === -1) return;

    let nextIndex = direction === "next" ? currentIndex + 1 : currentIndex - 1;
    if (nextIndex < 0) nextIndex = sortedFiltered.length - 1;
    if (nextIndex >= sortedFiltered.length) nextIndex = 0;

    const nextProduct = sortedFiltered[nextIndex];
    if (nextProduct) {
      handleOpenProductDetail(nextProduct);
    }
  };

  const filtered = products.filter((p) => {
    // Ocultar productos desactivados del catálogo público
    if (p.available === false) return false;

    // Excluir dulces especiales de temporada del catálogo público principal
    if (isSpecialEdition(p.category)) return false;

    const matchCat = activeCategory === "all" || p.category === activeCategory;

    const categoryName = categories.find((c) => c.id === p.category)?.name || "";

    const matchSearch =
      normalizeText(p.name).includes(normalizeText(search)) ||
      normalizeText(p.description).includes(normalizeText(search)) ||
      normalizeText(categoryName).includes(normalizeText(search)) ||
      // Buscar palabras individuales del nombre o categoría
      search
        .split(" ")
        .some(
          (word) =>
            word.length > 2 &&
            (normalizeText(p.name).includes(normalizeText(word)) ||
              normalizeText(categoryName).includes(normalizeText(word))),
        );
    return matchCat && matchSearch;
  });

  // Sort products to ensure Roscas appear first, respecting custom order if set
  const sortedFiltered = [...filtered].sort((a, b) => {
    // 1. Prioridad por orden personalizado (si existe)
    const hasOrdenA = a.orden !== undefined && a.orden !== null;
    const hasOrdenB = b.orden !== undefined && b.orden !== null;

    if (hasOrdenA && hasOrdenB) {
      return a.orden! - b.orden!;
    }
    if (hasOrdenA && !hasOrdenB) return -1;
    if (!hasOrdenA && hasOrdenB) return 1;

    // 2. Lógica por defecto (si no hay orden personalizado definido)
    const isRoscaA = a.id?.startsWith("rosca-") || a.name?.toLowerCase().includes("rosca");
    const isRoscaB = b.id?.startsWith("rosca-") || b.name?.toLowerCase().includes("rosca");

    // Prioritize Roscas
    if (isRoscaA && !isRoscaB) return -1;
    if (isRoscaB && !isRoscaA) return 1;
    if (isRoscaA && isRoscaB) {
      // If both are roscas, sort alphabetically by name
      return a.name.localeCompare(b.name);
    }

    // Prioritize Arreglo Floral
    if (a.id === "arreglo-floral-main") return -1;
    if (b.id === "arreglo-floral-main") return 1;

    // Then sort alphabetically within categories
    if (a.category !== b.category) return a.category.localeCompare(b.category);
    if (a.name !== b.name) return a.name.localeCompare(b.name);
    return 0;
  });

  // Calcular página segura sin useEffect
  const totalPages = Math.ceil(sortedFiltered.length / productsPerPage);
  const safePage = currentPage > totalPages ? 1 : currentPage;
  const startIndex = (safePage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;

  const currentProducts = sortedFiltered.slice(startIndex, endIndex);

  // Handlers que resetean la página directamente, sin useEffect
  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
    setCurrentPage(1);
  };

  const handleSearchChange = (val: string) => {
    setSearch(val);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll solo a la sección del catálogo, no al top de la página
    document.getElementById("catalog")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
    <section
      id="catalog"
      className="py-12 md:py-20 bg-transparent"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Section header */}
        <div className="text-center mb-12 reveal">
          <p className="font-body text-xs text-rose-500 tracking-[0.3em] uppercase font-medium mb-3">
            Nuestro Catálogo
          </p>
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-rose-900 mb-4">
            Sabores que enamoran
          </h2>
          <p className="font-body text-rose-700/70 text-base max-w-lg mx-auto">
            Cada torta es una obra de arte hecha a mano con los mejores
            ingredientes. Precios sujetos a cambios sin previo aviso.
          </p>
        </div>

        {/* Search bar */}
        <div className="relative max-w-md mx-auto mb-8 reveal">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-rose-400" />
          <input
            type="text"
            placeholder="Buscar por sabor..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            autoComplete="off"
            spellCheck="false"
            className="w-full pl-11 pr-10 py-3 rounded-2xl border border-rose-200 bg-white font-body text-sm text-rose-900 placeholder-rose-300 focus:outline-none focus:ring-2 focus:ring-rose-300 transition-all"
          />
          {search && (
            <button
              onClick={() => handleSearchChange("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-rose-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
              aria-label="Limpiar búsqueda">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Category tabs - horizontal scroll on mobile */}
        <div className="flex gap-2 overflow-x-auto pb-3 mb-10 scrollbar-hide reveal">
          <button
            onClick={() => handleCategoryChange("all")}
            className={`flex-shrink-0 font-body text-sm px-5 py-2 rounded-full transition-all duration-200 font-medium ${
              activeCategory === "all"
                ? "bg-rose-700 text-white shadow-md shadow-rose-200"
                : "bg-white text-rose-600 border border-rose-200 hover:border-rose-300"
            }`}>
            Todo
          </button>
          {categories.filter(cat => !isSpecialEdition(cat.id)).map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleCategoryChange(cat.id)}
              className={`flex-shrink-0 font-body text-sm px-5 py-2 rounded-full transition-all duration-200 font-medium flex items-center gap-1.5 ${
                activeCategory === cat.id
                  ? "bg-rose-700 text-white shadow-md shadow-rose-200"
                  : "bg-white text-rose-600 border border-rose-200 hover:border-rose-300"
              }`}>
              <span>{cat.emoji}</span>
              <span>{cat.name}</span>
            </button>
          ))}
        </div>

        {/* Category title */}
        {activeCategory !== "all" && (
          <div className="mb-6">
            {categories
              .filter((c) => c.id === activeCategory)
              .map((cat) => (
                <div key={cat.id} className="flex items-center gap-3">
                  <span className="text-3xl">{cat.emoji}</span>
                  <div>
                    <h3 className="font-display text-2xl text-rose-900 font-semibold">
                      {cat.name}
                    </h3>
                    <p className="font-body text-sm text-rose-500">
                      {cat.description}
                    </p>
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-rose-600 animate-spin mb-4" />
            <p className="font-body text-rose-700">Cargando catálogo...</p>
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <AlertCircle className="w-8 h-8 text-red-500 mb-4" />
            <p className="font-display text-xl text-red-700 mb-2">Error</p>
            <p className="font-body text-sm text-red-600 text-center max-w-md">
              {error}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-6 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors font-body text-sm">
              Reintentar
            </button>
          </div>
        )}

        {/* Products grid */}
        {!loading && !error && filtered.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 items-stretch">
              {/* Sin clase "reveal" en las cards — el IntersectionObserver las ocultaba al re-renderizar */}
              {currentProducts.map((product) => (
                <div key={`${product.id}-${safePage}`} className="h-full flex flex-col">
                  <ProductCard
                    product={product}
                    onOpenDetail={handleOpenProductDetail}
                  />
                </div>
              ))}
            </div>

            {/* Pagination controls */}
            {totalPages > 1 && (
              <div className="flex flex-col items-center mt-10 space-y-4">
                <div className="flex items-center gap-2 text-sm text-rose-600">
                  <span>
                    Mostrando {startIndex + 1}–
                    {Math.min(startIndex + productsPerPage, filtered.length)} de{" "}
                    {filtered.length} productos
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(safePage - 1)}
                    disabled={safePage === 1}
                    className={`p-2 rounded-lg transition-all ${
                      safePage === 1
                        ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                        : "bg-white text-rose-600 hover:bg-rose-50 border border-rose-200"
                    }`}
                    aria-label="Página anterior">
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`w-8 h-8 rounded-lg font-body text-sm font-medium transition-all ${
                            safePage === page
                              ? "bg-rose-700 text-white shadow-md"
                              : "bg-white text-rose-600 hover:bg-rose-50 border border-rose-200"
                          }`}>
                          {page}
                        </button>
                      ),
                    )}
                  </div>

                  <button
                    onClick={() => handlePageChange(safePage + 1)}
                    disabled={safePage === totalPages}
                    className={`p-2 rounded-lg transition-all ${
                      safePage === totalPages
                        ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                        : "bg-white text-rose-600 hover:bg-rose-50 border border-rose-200"
                    }`}
                    aria-label="Página siguiente">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          !loading &&
          !error && (
            <div className="text-center py-20">
              <p className="text-5xl mb-4">🔍</p>
              <p className="font-display text-xl text-rose-700">
                No encontramos resultados
              </p>
              <p className="font-body text-sm text-rose-600 mt-1">
                Intenta con otra búsqueda
              </p>
            </div>
          )
        )}
      </div>

    </section>

    {/* Modal de Detalle de Postre — Portal para escapar el stacking context de la sección */}
    {selectedProductForDetail && ReactDOM.createPortal(
      <ProductDetailModal
        product={selectedProductForDetail}
        isOpen={Boolean(selectedProductForDetail)}
        onClose={handleCloseProductDetail}
        onNavigateNext={() => handleNavigateDetail("next")}
        onNavigatePrev={() => handleNavigateDetail("prev")}
      />,
      document.body
    )}
  </>);
};

export default Catalog;
